import { useSyncExternalStore } from 'react';
import api from '../lib/api';
import { getStoredCustomerAuthToken } from './auth/customerAuthToken';

type UnreadCountResponse = {
  unread_count?: number;
};

let unreadMessageCount = 0;
let unreadCountToken: string | null = null;
let syncVersion = 0;
const listeners = new Set<() => void>();

const getUnreadCount = (): number => (
  unreadCountToken === getStoredCustomerAuthToken() ? unreadMessageCount : 0
);

const subscribeUnreadCount = (listener: () => void): (() => void) => {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
};

export const useUnreadMessageCount = (): number => (
  useSyncExternalStore(subscribeUnreadCount, getUnreadCount, () => 0)
);

const getNavigatorWithBadge = (): (Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}) | null => {
  if (typeof navigator === 'undefined') return null;
  return navigator;
};

export const syncAppBadge = async (unreadCount: number): Promise<void> => {
  syncVersion += 1;
  unreadMessageCount = Number.isFinite(unreadCount) ? Math.max(0, Math.floor(unreadCount)) : 0;
  unreadCountToken = getStoredCustomerAuthToken();
  listeners.forEach((listener) => listener());
  unreadCount = unreadMessageCount;

  const nav = getNavigatorWithBadge();
  if (!nav || typeof nav.setAppBadge !== 'function') return;

  try {
    if (unreadCount > 0) {
      await nav.setAppBadge(unreadCount);
      return;
    }

    if (typeof nav.clearAppBadge === 'function') {
      await nav.clearAppBadge();
      return;
    }

    await nav.setAppBadge(0);
  } catch (error) {
    console.error('syncAppBadge error:', error);
  }
};

export const syncUnreadAppBadge = async (): Promise<number | null> => {
  const token = getStoredCustomerAuthToken();
  if (!token) {
    await syncAppBadge(0);
    return null;
  }
  const requestVersion = ++syncVersion;

  try {
    const { data } = await api.get<UnreadCountResponse>('/api/customer-app/messages/unread-count');
    // Discard responses from an older session or a request predating a read action.
    if (token !== getStoredCustomerAuthToken() || requestVersion !== syncVersion) return null;
    const unreadCount = Math.max(0, Math.floor(Number(data.unread_count ?? 0) || 0));
    await syncAppBadge(unreadCount);
    return unreadCount;
  } catch (error) {
    console.error('syncUnreadAppBadge error:', error);
    return null;
  }
};
